import Dexie from "dexie";
import { normalizeDocument, exportDocument, UnsupportedFormatError } from "./document.js";

export class ConflictError extends Error {
  constructor() {
    super(
      "This canvas changed in another tab or device. Save your work as a copy or reload the saved canvas.",
    );
    this.name = "ConflictError";
  }
}

/** IndexedDB is private to this module. Every write returns only after its transaction commits. */
export function createLibrary({
  name = "architecture-canvas-v1",
  indexedDB,
  IDBKeyRange,
  checkpointInterval = 30000,
  checkpointLimit = 15,
  checkpointBytes = 20 * 1024 * 1024,
} = {}) {
  const db = new Dexie(
    name,
    indexedDB ? { indexedDB, IDBKeyRange } : undefined,
  );
  db.version(1).stores({
    canvases: "id, updatedAt, deletedAt",
    checkpoints: "id, canvasId, createdAt",
    preferences: "key",
  });
  db.version(2).stores({
    canvases: "id, folderId, updatedAt, deletedAt",
    checkpoints: "id, canvasId, createdAt",
    preferences: "key",
    folders: "id, &nameKey, createdAt",
  }).upgrade(async (tx) => {
    const records = await tx.table("canvases").toArray();
    for (const record of records) {
      record.folderId = await legacyFolderId(record.payload?.project, tx.table("folders"));
      await tx.table("canvases").put(record);
    }
  });
  // Dexie can reopen newer database versions, so a version bump alone cannot
  // keep legacy checkpoint recovery from overwriting comment-bearing records.
  // Move documents and their recovery history atomically into separate stores.
  db.version(3).stores({
    canvases: null,
    checkpoints: null,
    documents: "id, folderId, updatedAt, deletedAt",
    documentCheckpoints: "id, canvasId, createdAt",
    preferences: "key",
    folders: "id, &nameKey, createdAt",
  }).upgrade(async (tx) => {
    await tx.table("documents").bulkAdd(await tx.table("canvases").toArray());
    await tx.table("documentCheckpoints").bulkAdd(await tx.table("checkpoints").toArray());
  });
  db.on("versionchange", () => db.close());

  // Both legacy entry paths share one mapping and execute inside their migration transaction.
  async function legacyFolderId(project, table) {
    const name = typeof project === "string" ? project.trim() : "";
    const nameKey = name.toLowerCase();
    if (!name || ["my canvases", "unfiled"].includes(nameKey)) return null;
    const existing = await table.where("nameKey").equals(nameKey).first();
    if (existing) return existing.id;
    const folder = { id: crypto.randomUUID(), name, nameKey, createdAt: Date.now() };
    await table.add(folder);
    return folder.id;
  }

  const getPreference = async (key) => (await db.preferences.get(key))?.value;
  const setPreference = (key, value) => db.preferences.put({ key, value });
  async function list({ trash = false } = {}) {
    return (await db.documents.toArray())
      .filter((row) => Boolean(row.deletedAt) === trash)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .map(({ id, title, updatedAt, revision, deletedAt, nodeCount, folderId }) => ({
        id,
        title,
        updatedAt,
        revision,
        deletedAt,
        nodeCount,
        folderId: folderId || null,
      }));
  }
  function validateFolderName(value) {
    const name = String(value ?? "").trim();
    if (!name || name.length > 80) throw new Error("Use a folder name between 1 and 80 characters.");
    return { name, nameKey: name.toLowerCase() };
  }
  async function listFolders() {
    return (await db.folders.toArray()).sort((a, b) => a.createdAt - b.createdAt || a.name.localeCompare(b.name));
  }
  async function createFolder(value) {
    const folder = { id: crypto.randomUUID(), ...validateFolderName(value), createdAt: Date.now() };
    try {
      await db.folders.add(folder);
    } catch (error) {
      if (error.name === "ConstraintError") throw new Error("A folder with this name already exists.");
      throw error;
    }
    return folder;
  }
  async function renameFolder(id, value) {
    const name = validateFolderName(value);
    try {
      const updated = await db.folders.update(id, name);
      if (!updated) throw new Error("Folder not found.");
    } catch (error) {
      if (error.name === "ConstraintError") throw new Error("A folder with this name already exists.");
      throw error;
    }
  }
  async function history(id) {
    return (await db.documentCheckpoints.where("canvasId").equals(id).toArray()).sort(
      (a, b) =>
        b.createdAt - a.createdAt || (b.revision || 0) - (a.revision || 0),
    );
  }
  async function open(id) {
    const row = await db.documents.get(id);
    if (!row) throw new Error("Canvas not found.");
    try {
      return { ...row, doc: normalizeDocument(row.payload), recovered: false };
    } catch (error) {
      if (error instanceof UnsupportedFormatError) throw error;
      for (const checkpoint of await history(id)) {
        try {
          return {
            ...row,
            doc: normalizeDocument(checkpoint.payload),
            recovered: true,
          };
        } catch {}
      }
      throw new Error(
        `“${row.title}” could not be opened and has no readable checkpoint. Import a JSON backup to recover it.`,
        { cause: error },
      );
    }
  }
  async function writeCheckpoint(record, label) {
    const checkpoint = {
      id: crypto.randomUUID(),
      canvasId: record.id,
      createdAt: Date.now(),
      revision: record.revision,
      label,
      payload: record.payload,
    };
    checkpoint.bytes = JSON.stringify(checkpoint.payload).length * 2;
    await db.documentCheckpoints.add(checkpoint);
    // Pruning and the new checkpoint are atomic; a failed transaction cannot discard recovery data.
    const entries = await history(record.id);
    let bytes = 0;
    for (let i = 0; i < entries.length; i++) {
      bytes += entries[i].bytes || 0;
      if (i > 0 && (i >= checkpointLimit || bytes > checkpointBytes))
        await db.documentCheckpoints.delete(entries[i].id);
    }
  }
  async function create(document, { view = null, reason = "Created", folderId = null } = {}) {
    const doc = normalizeDocument(exportDocument(document));
    doc.id = crypto.randomUUID();
    const now = Date.now(),
      record = {
        id: doc.id,
        title: doc.title,
        payload: exportDocument(doc),
        revision: 1,
        createdAt: now,
        updatedAt: now,
        lastCheckpointAt: now,
        deletedAt: 0,
        nodeCount: doc.nodes.length,
        view,
        folderId,
      };
    await db.transaction(
      "rw",
      db.documents,
      db.documentCheckpoints,
      db.preferences,
      db.folders,
      async () => {
        if (folderId && !(await db.folders.get(folderId))) throw new Error("Folder not found.");
        await db.documents.add(record);
        await writeCheckpoint(record, reason);
        await setPreference("lastCanvasId", record.id);
      },
    );
    return { ...record, doc };
  }
  async function save(
    document,
    { expectedRevision, view = null, checkpointLabel } = {},
  ) {
    const doc = normalizeDocument(exportDocument(document));
    const now = Date.now();
    return db.transaction("rw", db.documents, db.documentCheckpoints, async () => {
      const previous = await db.documents.get(doc.id);
      if (
        !previous ||
        previous.deletedAt ||
        previous.revision !== expectedRevision
      )
        throw new ConflictError();
      const record = {
        ...previous,
        title: doc.title,
        payload: exportDocument(doc),
        revision: previous.revision + 1,
        updatedAt: now,
        nodeCount: doc.nodes.length,
        view: view || previous.view,
      };
      if (checkpointLabel) {
        await writeCheckpoint(record, checkpointLabel);
        record.lastCheckpointAt = now;
      } else if (now - previous.lastCheckpointAt >= checkpointInterval) {
        await writeCheckpoint(previous, "Autosave");
        record.lastCheckpointAt = now;
      }
      await db.documents.put(record);
      return { revision: record.revision, updatedAt: now };
    });
  }
  async function saveView(id, view) {
    await db.documents.update(id, { view });
  }
  async function moveCanvas(id, folderId, expectedRevision) {
    return db.transaction("rw", db.documents, db.folders, async () => {
      const previous = await db.documents.get(id);
      if (!previous || previous.deletedAt || previous.revision !== expectedRevision) throw new ConflictError();
      if (folderId && !(await db.folders.get(folderId))) throw new Error("Folder not found.");
      const result = { folderId: folderId || null, revision: previous.revision + 1, updatedAt: Date.now() };
      await db.documents.update(id, result);
      return result;
    });
  }
  async function trash(id, expectedRevision) {
    return db.transaction("rw", db.documents, db.documentCheckpoints, async () => {
      const record = await db.documents.get(id);
      if (!record || record.revision !== expectedRevision)
        throw new ConflictError();
      await writeCheckpoint(record, "Before moving to trash");
      await db.documents.update(id, {
        deletedAt: Date.now(),
        revision: record.revision + 1,
      });
    });
  }
  async function restore(id) {
    await db.documents.update(id, { deletedAt: 0, updatedAt: Date.now() });
    return open(id);
  }
  async function checkpointDocument(id) {
    const checkpoint = await db.documentCheckpoints.get(id);
    if (!checkpoint) throw new Error("Checkpoint not found.");
    return normalizeDocument(checkpoint.payload);
  }
  async function migratePrototype(payload, view) {
    if (await getPreference("prototypeImported")) return null;
    if (!payload) return null;
    // The deterministic id and marker in one transaction make simultaneous-tab migration idempotent.
    const doc = normalizeDocument(payload);
    doc.id = "imported-obsidian-prototype";
    const now = Date.now();
    return db.transaction(
      "rw",
      db.documents,
      db.documentCheckpoints,
      db.preferences,
      db.folders,
      async () => {
        if (await getPreference("prototypeImported")) return null;
        const record = {
          id: doc.id,
          title: doc.title,
          payload: exportDocument(doc),
          revision: 1,
          createdAt: now,
          updatedAt: now,
          lastCheckpointAt: now,
          deletedAt: 0,
          nodeCount: doc.nodes.length,
          view,
          folderId: await legacyFolderId(doc.project, db.folders),
        };
        await db.documents.put(record);
        await writeCheckpoint(record, "Imported from prototype");
        await setPreference("prototypeImported", true);
        await setPreference("lastCanvasId", doc.id);
        return record.id;
      },
    );
  }
  return {
    async exportForCloud() {
      return db.transaction('rw', db.preferences, db.documents, db.folders, async () => {
        let sourceId = await getPreference('cloudImportSourceId');
        if (!sourceId) {
          sourceId = crypto.randomUUID();
          await setPreference('cloudImportSourceId', sourceId);
        }
        const canvases = await db.documents.toArray();
        // Validate every record before sending anything. Never mutate local originals.
        for (const row of canvases) row.payload = exportDocument(normalizeDocument(row.payload));
        return { sourceId, canvases, folders: await db.folders.toArray() };
      });
    },
    listFolders,
    createFolder,
    renameFolder,
    moveCanvas,
    list,
    open,
    create,
    save,
    saveView,
    history,
    trash,
    restore,
    checkpointDocument,
    getPreference,
    setPreference,
    migratePrototype,
    close: () => db.close(),
  };
}

export { createSaveQueue } from "./save-queue.js";
